import { Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { isComplexValue, isFilterEmpty } from '@modules/filter/utils/filter-changes';
import { Filter, FilterBean, FilterQ } from '@type/filter.type';
import { distinctUntilChanged, map, merge, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  #filter = new Map<string, FilterBean>();

  #filterChangeTime = signal(Date.now());

  #filterTimeChanges = toObservable(this.#filterChangeTime).pipe(distinctUntilChanged());

  #resetFilterForRequestState = new Subject<undefined>();

  changes = this.#filterTimeChanges.pipe(
    map(() => Array.from(this.#filter.entries()).filter(([key, filter]) => !isFilterEmpty(filter))),
  );

  private processFilter = (filter: [string, FilterBean<{ id: number }>][]): FilterQ => {
    let result: { q: Record<string, any> } & Record<string, any> = { q: {} };
    const q = {};

    for (const [k, b] of filter) {
      if (Number(k)) break;
      const model = b.model || k.split(' ').join('_');
      const v = FilterBean.getFilter(b);
      Object.assign(q, { [model.toLowerCase()]: v });
    }

    if (this.filledObject(q)) Object.assign(result, { q });

    return result;
  };

  resetFilterForRequest = () => this.#resetFilterForRequestState.next(undefined);

  filterForRequest = toSignal(merge(this.changes.pipe(map(this.processFilter)), this.#resetFilterForRequestState));

  set(filter: Filter) {
    this.#filter.clear();
    Object.entries(filter).forEach(([k, v]) => isComplexValue(v) && this.#filter.set(k, v));
    this.tick();
  }

  get() {
    return Object.fromEntries(this.#filter.entries());
  }

  getFilter<T>(key: string): T | null {
    return this.#filter.get(key) as T;
  }

  setFilter<T extends FilterBean>(key: string, filter: T) {
    this.#filter.set(key, filter);
  }

  tick() {
    this.#filterChangeTime.set(Date.now());
  }

  filledArray(arr: any[] | null | undefined) {
    return arr && Boolean(arr.length);
  }

  filledObject(obj: object | null | undefined) {
    return Boolean(obj && Object.keys(obj).length);
  }
}
